<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import {
  NCard, NInput, NButton, NAlert, NSpace, NText,
  NForm, NFormItem, NInputGroup, NSpin
} from 'naive-ui'

import { fetchProviderProfile } from '@/services/provider'
import { verify, resendVerification, getCurrentUser, currentUser } from '@/services/auth'
import { dt } from '@/utils/i18n'

const router = useRouter()
const route = useRoute()

// Reactive state
const verificationCode = ref('')
const userId = ref(localStorage.getItem('pending_user_id'))
const email = ref(localStorage.getItem('pending_verification_email') || '')
const validationType = ref(localStorage.getItem('pending_validation_type') || 'login')
const preferredLanguage = ref(localStorage.getItem('preferred_language') || 'english')

const submitStatus = ref(null)
const errorMessage = ref('')
const resendStatus = ref(null)
const resendMessage = ref('')
const showRedirectSpinner = ref(false)

const returnUrl = computed(() => route.query.returnUrl || '/')

// Verify user
const handleVerify = async () => {
  if (!userId.value || !verificationCode.value) {
    errorMessage.value = 'Missing user information or verification code'
    submitStatus.value = 'error'
    return
  }

  try {
    submitStatus.value = 'submitting'
    await verify(userId.value, verificationCode.value, validationType.value, preferredLanguage.value)

    // Wait for user data to load completely
    try {
      await getCurrentUser()

      if (currentUser.value?.role) {
        localStorage.setItem('user_role', currentUser.value.role)
      }
    } catch (userError) {
      console.error('Failed to load user data:', userError)
      errorMessage.value = 'Failed to load user profile. Please try logging in again.'
      submitStatus.value = 'error'
      return
    }

    // Clean up localStorage
    localStorage.removeItem('pending_verification_email')
    localStorage.removeItem('pending_user_id')
    localStorage.removeItem('pending_validation_type')

    // Show redirect spinner
    showRedirectSpinner.value = true

    // Determine redirect based on role
    let redirectPath = '/'

    if (currentUser.value?.role === 'provider') {
      // Check if provider profile exists
      try {
        const token = localStorage.getItem('auth_token')
        const profileData = await fetchProviderProfile(token)

        if (profileData.status === 'not_found') {
          redirectPath = '/provider/manage'
        } else {
          redirectPath = '/provider-profile'
        }
      } catch (error) {
        redirectPath = '/provider/manage'
      }
    } else if (currentUser.value?.role === 'admin') {
      redirectPath = '/admin/dashboard'
    } else if (currentUser.value?.role === 'validator') {
      redirectPath = '/validator/dashboard'
    } else {
      // Patient or default
      redirectPath = returnUrl.value || '/'
    }

    setTimeout(() => {
      router.push(redirectPath)
    }, 1500)
  } catch (error) {
    console.error('Verification error:', error)
    submitStatus.value = 'error'
    errorMessage.value = error.message || 'Verification failed'
  }
}

// Resend verification code
const handleResend = async () => {
  if (!userId.value) {
    resendStatus.value = 'error'
    resendMessage.value = 'Missing user information'
    return
  }

  try {
    resendStatus.value = 'submitting'
    const result = await resendVerification(userId.value)

    if (result.verification_type) {
      validationType.value = result.verification_type
      localStorage.setItem('pending_validation_type', result.verification_type)
    }

    if (result.user_id) {
      userId.value = result.user_id
      localStorage.setItem('pending_user_id', result.user_id)
    }

    resendStatus.value = 'success'
    resendMessage.value = result.message || 'Verification code resent'
  } catch (error) {
    resendStatus.value = 'error'
    resendMessage.value = error.message || 'Failed to resend verification code'
  }
}

// Navigate to login
const goToLogin = () => {
  router.push({
    name: 'login',
    query: { returnUrl: returnUrl.value }
  })
}

// On mount, redirect if necessary
onMounted(() => {
  if (!userId.value || !email.value) {
    goToLogin()
  }
})
</script>

<template>
  <!-- Full-page spinner overlay -->
  <div v-if="showRedirectSpinner" class="redirect-spinner-overlay">
    <NSpin size="large" />
    <div class="spinner-message">
      <p>{{ dt('verify.successMessage') }}</p>
      <p>{{ dt('verify.redirectMessage') }}</p>
    </div>
  </div>

  <div v-else class="auth-page">
    <div class="auth-container">
      <NCard :title="dt('verify.title')" class="auth-card">
        <div class="card-content">

          <!-- Error messages -->
          <div v-if="submitStatus === 'error'" class="mb-4">
            <NAlert type="error" :title="errorMessage" />
          </div>
          <div v-if="resendStatus === 'success'" class="mb-4">
            <NAlert type="success" :title="resendMessage" />
          </div>
          <div v-if="resendStatus === 'error'" class="mb-4">
            <NAlert type="error" :title="resendMessage" />
          </div>

          <div class="verification-info">
            <p>{{ dt('verify.subtitle', { email }) }}</p>
            <p>{{ dt('verify.instruction') }}</p>
          </div>

          <NForm>
            <NFormItem :label="dt('verify.codeLabel')" required>
              <NInput v-model:value="verificationCode" type="text" :placeholder="dt('verify.codePlaceholder')"
                :disabled="submitStatus === 'submitting'" @keydown.enter="handleVerify" maxlength="7" />
            </NFormItem>

            <div class="form-actions">
              <NButton type="primary" block :loading="submitStatus === 'submitting'"
                :disabled="!verificationCode || verificationCode.length < 7" @click="handleVerify">
                {{ dt('verify.verifyButton') }}
              </NButton>
            </div>
          </NForm>

          <div class="resend-option">
            <NButton text @click="handleResend" :disabled="resendStatus === 'submitting'"
              :loading="resendStatus === 'submitting'">
              {{ dt('verify.resendPrompt') }} {{ dt('verify.resendLink') }}
            </NButton>
          </div>

          <div class="auth-options">
            <NButton text @click="goToLogin">{{ dt('verify.backToLogin') }}</NButton>
          </div>
        </div>
      </NCard>

      <div class="auth-info">
        <h2>{{ dt('verify.rightPanelTitle') }}</h2>
        <p>{{ dt('verify.rightPanelInstruction') }}</p>

        <div class="auth-steps">
          <div class="auth-step completed">
            <div class="step-number">1</div>
            <div class="step-text">{{ dt('verify.step1') }}</div>
          </div>
          <div class="auth-step active">
            <div class="step-number">2</div>
            <div class="step-text">{{ dt('verify.step2') }}</div>
          </div>
          <div class="auth-step">
            <div class="step-number">3</div>
            <div class="step-text">{{ dt('verify.step3') }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Existing styles remain unchanged */

.auth-page {
  min-height: calc(100vh - 200px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: #f8f9fa;
}

.auth-container {
  display: flex;
  max-width: 900px;
  width: 100%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
}

.auth-card {
  flex: 1;
  border-radius: 0;
  box-shadow: none;
}

.card-content {
  padding: 1rem 0;
}

.verification-info {
  margin-bottom: 2rem;
  text-align: center;
}

.verification-info p {
  margin-bottom: 0.5rem;
  color: #666;
}

.form-actions {
  margin-top: 2rem;
}

.resend-option {
  text-align: center;
  margin-top: 1.5rem;
}

.auth-options {
  margin-top: 2rem;
  text-align: center;
}

.auth-info {
  flex: 1;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.auth-info h2 {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.auth-info p {
  font-size: 1.1rem;
  margin-bottom: 2.5rem;
  opacity: 0.9;
}

.auth-steps {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.auth-step {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.step-number {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-weight: bold;
  background: rgba(255, 255, 255, 0.2);
}

.step-text {
  font-size: 1.1rem;
}

.auth-step.completed .step-number {
  background: rgba(255, 255, 255, 0.8);
  color: #764ba2;
}

.auth-step.active .step-number {
  background: white;
  color: #764ba2;
}

.auth-step.completed .step-text,
.auth-step.active .step-text {
  font-weight: bold;
}

.mb-4 {
  margin-bottom: 1rem;
}

.redirect-spinner-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #f8f9fa;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.spinner-message {
  margin-top: 24px;
  text-align: center;
}

.spinner-message p {
  margin: 8px 0;
  font-size: 18px;
  color: #667eea;
}

.spinner-message p:first-child {
  font-weight: bold;
  font-size: 22px;
  color: #764ba2;
}

@media (max-width: 768px) {
  .auth-container {
    flex-direction: column-reverse;
  }

  .auth-info {
    padding: 2rem;
  }
}
</style>
